import { Fragment, useState, useEffect } from "react";
import { Dialog, Menu } from "@headlessui/react";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import Avatar from "./Avatar";
import useAuthStore from "../../stores/authStore";
import { formatCNPJ } from "../../utils/format";

// Constantes de estilo
const MENU_ITEM_CLASS = "block px-4 py-2 text-sm text-slate-300 hover:bg-slate-800 hover:text-white";
const MENU_ITEM_ACTIVE_CLASS = "bg-slate-800 text-white";

const Logo = ({ onClick }) => (
  <Link to="/" className="-m-1.5 p-1.5" onClick={onClick}>
    <span className="sr-only">Sistema IRRF</span>
    <img src="/images/logo_RI.png" alt="IRRF Logo" className="h-10 w-auto" />
  </Link>
);

const MenuItem = ({ to, onClick, children }) => (
  <Menu.Item>
    {({ active }) =>
      to ? (
        <Link
          to={to}
          className={`${active ? MENU_ITEM_ACTIVE_CLASS : ""} ${MENU_ITEM_CLASS}`}
          onClick={onClick}
        >
          {children}
        </Link>
      ) : (
        <button
          onClick={onClick}
          className={`${active ? MENU_ITEM_ACTIVE_CLASS : ""} ${MENU_ITEM_CLASS} w-full text-left`}
        >
          {children}
        </button>
      )
    }
  </Menu.Item>
);

const UserMenu = ({ user, logout, onClose }) => {
  // Pega os dados do usuário
  const displayName = user?.tradeName || user?.email || "";
  const displayCNPJ = user?.cnpj || "00.000.000/0000-00";

  return (
    <Menu as="div" className="relative">
      <Menu.Button className="flex items-center gap-2 text-slate-300 hover:text-white">
        <Avatar url={user?.avatarUrl} size="sm" alt={displayName} />
        <div className="flex flex-col items-start">
          <span className="text-sm font-medium text-slate-200">
            {displayName}
          </span>
          <span className="text-xs text-slate-400">
            {formatCNPJ(displayCNPJ)}
          </span>
        </div>
      </Menu.Button>
      <Menu.Items className="absolute right-0 mt-2 w-48 origin-top-right bg-slate-900 border border-slate-700 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none divide-y divide-slate-700">
        <div className="py-1">
          <MenuItem
            to={user?.role === "ADMIN" ? "/admin/dashboard" : "/user/dashboard"}
            onClick={onClose}
          >
            Dashboard
          </MenuItem>
          <MenuItem to="/profile" onClick={onClose}>
            Perfil
          </MenuItem>
          <MenuItem
            onClick={() => {
              logout();
              onClose?.();
            }}
          >
            Sair
          </MenuItem>
        </div>
      </Menu.Items>
    </Menu>
  );
};

const AuthContent = ({
  isAuthenticated,
  user,
  logout,
  onAuthClick,
  onClose,
}) => {
  if (isAuthenticated && user) {
    return <UserMenu user={user} logout={logout} onClose={onClose} />;
  }

  return (
    <button
      onClick={() => {
        onClose?.();
        onAuthClick();
      }}
      className="text-sm font-medium text-slate-300 hover:text-white transition-colors duration-200"
    >
      Entrar
    </button>
  );
};

const MobileMenu = ({ isOpen, onClose, children }) => (
  <Dialog as="div" className="lg:hidden" open={isOpen} onClose={onClose}>
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" />
    <Dialog.Panel className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-slate-900 px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-slate-800">
      <div className="flex items-center justify-between">
        <Logo onClick={onClose} />
        <button
          type="button"
          className="-m-2.5 rounded-md p-2.5 text-slate-400 hover:text-white"
          onClick={onClose}
        >
          <span className="sr-only">Fechar menu</span>
          <XMarkIcon className="h-6 w-6" aria-hidden="true" />
        </button>
      </div>
      <div className="mt-6 flow-root">
        <div className="py-6">{children}</div>
      </div>
    </Dialog.Panel>
  </Dialog>
);

const Header = ({ onAuthClick }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { user, isAuthenticated, logout } = useAuthStore();

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleClose = () => setMobileMenuOpen(false);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? "bg-slate-900/95 backdrop-blur-md shadow-lg border-b border-slate-800" 
          : "bg-slate-900/50 backdrop-blur-sm border-b border-transparent"
      }`}
    >
      <nav
        className="flex items-center justify-between p-6 lg:px-8"
        aria-label="Global"
      >
        <div className="flex lg:flex-1">
          <Logo />
        </div>
        <div className="flex lg:hidden">
          <button
            type="button"
            className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-slate-400 hover:text-white"
            onClick={() => setMobileMenuOpen(true)}
          >
            <span className="sr-only">Abrir menu principal</span>
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
        <div className="hidden lg:flex lg:flex-1 lg:justify-end">
          <AuthContent
            isAuthenticated={isAuthenticated}
            user={user}
            logout={logout}
            onAuthClick={onAuthClick}
          />
        </div>
      </nav>
      <MobileMenu isOpen={mobileMenuOpen} onClose={handleClose}>
        <AuthContent
          isAuthenticated={isAuthenticated}
          user={user}
          logout={logout}
          onAuthClick={onAuthClick}
          onClose={handleClose}
        />
      </MobileMenu>
    </header>
  );
};

export default Header;
